import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BugReportService } from '../../../core/services/bug-report.service';
import { PlaybackService } from '../../../core/services/playback.service';
import { ConsoleBufferService } from '../../../core/services/console-buffer.service';
import { SpeechRecognitionService } from '../../../core/services/speech-recognition.service';

@Component({
  selector: 'app-bug-report-dialog',
  templateUrl: './bug-report-dialog.component.html',
  styleUrls: ['./bug-report-dialog.component.scss']
})
export class BugReportDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isSubmitting = false;
  isRecording = false;
  recognition: any;
  lastFinalResult = '';
  logCount = 0;

  private openedAt = '';
  private contextPromise: Promise<object> | null = null;
  private playbackService = inject(PlaybackService);

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<BugReportDialogComponent>,
    private bugReportService: BugReportService,
    private speechService: SpeechRecognitionService,
    private snackBar: MatSnackBar
  ) {
    this.form = this.fb.group({
      // pattern(/\S/) rejects whitespace-only input, matching the backend's trimmed check.
      description: ['', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(5000)]]
    });
  }

  ngOnInit(): void {
    // Mic dictation pauses the stream (Android audio focus); that pause is an
    // artifact of reporting, not the bug — recovery must not fight it.
    this.playbackService.holdRecovery('bug-report-dialog');
    // Snapshot context at open time so it reflects the state the user is reporting.
    this.openedAt = new Date().toISOString();
    this.logCount = ConsoleBufferService.getBuffer().length;
    this.contextPromise = this.bugReportService.collectContext();
    this.initializeSpeechRecognition();
  }

  ngOnDestroy(): void {
    this.stopRecording();
    this.playbackService.releaseRecovery();
  }

  get isSpeechSupported(): boolean {
    return this.speechService.isSupported();
  }

  private initializeSpeechRecognition(): void {
    if (!this.speechService.isSupported()) {
      return;
    }

    this.recognition = this.speechService.createRecognition({
      continuous: false,
      interimResults: true,
      language: 'en-US'
    });

    this.recognition.onstart = () => {
      this.isRecording = true;
      this.lastFinalResult = '';
    };

    this.recognition.onresult = (event: any) => {
      this.processResults(event);
    };

    this.recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      this.isRecording = false;
    };

    this.recognition.onend = () => {
      this.isRecording = false;
    };
  }

  async startRecording(): Promise<void> {
    if (!this.speechService.isSupported()) {
      return;
    }

    const hasPermission = await this.speechService.requestPermission();
    if (!hasPermission) {
      return;
    }

    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.recognition.start();
    }
  }

  stopRecording(): void {
    if (this.recognition && this.isRecording) {
      this.recognition.stop();
      this.isRecording = false;
    }
  }

  private processResults(event: any): void {
    let final = '';

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      const transcript = result[0].transcript;
      const confidence = result[0].confidence;

      // Check if this is truly a final result (confidence > 0)
      // On mobile browsers, interim results are sometimes marked as final but have confidence = 0
      const isTrueFinal = result.isFinal && (confidence === undefined || confidence > 0);

      if (isTrueFinal) {
        final += transcript;
      }
    }

    // Only process final results to avoid duplication
    if (final && final !== this.lastFinalResult) {
      this.lastFinalResult = final;
      const currentText = this.form.get('description')?.value || '';
      const separator = currentText && !currentText.endsWith(' ') ? ' ' : '';
      this.form.patchValue({
        description: currentText + separator + final
      });
    }
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    let reportId: string;
    let context: object;
    try {
      reportId = this.generateReportId();
      // The context must always be an object per the API contract, even when collection failed.
      context = await (this.contextPromise ?? Promise.resolve({}))
        .catch(() => ({ collectError: 'context collection failed' }));
    } catch (error) {
      console.error('Failed to build bug report payload:', error);
      this.snackBar.open('Failed to send bug report', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      this.isSubmitting = false;
      return;
    }

    this.bugReportService.submit({
      reportId,
      description: (this.form.get('description')?.value || '').trim(),
      openedAt: this.openedAt,
      submittedAt: new Date().toISOString(),
      timezoneOffsetMinutes: new Date().getTimezoneOffset(),
      context
    }).subscribe({
      next: () => {
        this.snackBar.open('Bug report sent · ' + reportId.slice(0, 8), 'Close', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Failed to submit bug report:', error);
        this.snackBar.open('Failed to send bug report', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
        this.isSubmitting = false;
      }
    });
  }

  /**
   * crypto.randomUUID() only exists in secure contexts; plain-HTTP dev access
   * (e.g. via LAN IP without --ssl) needs the getRandomValues fallback.
   */
  private generateReportId(): string {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
}
